import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import Ring from "@/components/Ring";
import { PromisesPanel } from "@/components/app/PromisesPanel";
import { PromiseSuggestions } from "@/components/app/PromiseSuggestions";
import * as L from "lucide-react";

export function HomeScreen({ user, device, go, resumeConversation }) {
  const greet = greeting();
  const [today, setToday] = useState({ connected: false, events: [] });
  const [notes, setNotes] = useState({ loading: true, items: [] });
  const [gmail, setGmail] = useState({ loading: true, items: [], error: null });
  const [onThisDay, setOnThisDay] = useState(null);
  const [resume, setResume] = useState(null);
  const [dueSummary, setDueSummary] = useState(null);
  const [pendingCheckins, setPendingCheckins] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [promises, setPromises] = useState([]);
  const [promiseSuggestions, setPromiseSuggestions] = useState(null);

  const loadPromises = () => api.get("/commitments")
    .then(r => setPromises([...(r.data?.slipped || []), ...(r.data?.open || [])].slice(0, 6)))
    .catch(() => {});
  const loadPromiseSuggestions = () => api.get("/promises/suggestions")
    .then(r => setPromiseSuggestions(r.data))
    .catch(() => {});

  useEffect(() => {
    api.get("/calendar/today").then(r => setToday(r.data)).catch(()=>{});
    api.get("/integrations/inbox").then(r => {
      setNotes({ loading: false, items: (r.data.items || []).slice(0, 3) });
    }).catch(() => setNotes({ loading: false, items: [] }));
    api.get("/gmail/inbox").then(r => {
      setGmail({
        loading: false,
        items: (r.data.items || []).slice(0, 3),
        error: r.data.connected === false ? (r.data.detail || "Google isn't connected.") : null,
      });
    }).catch(e => {
      setGmail({ loading: false, items: [], error: e?.response?.data?.detail || null });
    });
    api.get("/memories/on-this-day").then(r => setOnThisDay(r.data)).catch(()=>{});
    api.get("/conversations/resume").then(r => setResume(r.data?.conversation || null)).catch(()=>{});
    api.get("/reminders/due-summary").then(r => {
      setDueSummary(r.data);
      setCommitments(r.data?.preview || []);
    }).catch(()=>{});
    // Emotional callbacks — silently ignored if the user has none pending.
    api.get("/checkins/pending").then(r => setPendingCheckins(r.data || [])).catch(() => {});
    loadPromises();
    loadPromiseSuggestions();
  }, [user?.calendar_ical_url]);

  const gmailNotConnected = gmail.error && gmail.error.toLowerCase().includes("google isn't connected");
  const firstName = user?.preferred_name || user?.name?.split(" ")[0] || "there";
  const thingCount = commitments.length + (today.events?.length || 0);

  // Voice is the primary object. Hand over to the chat screen's voice mode
  // through a one-shot sessionStorage flag so the Ring feels instant.
  const openVoice = () => {
    try { sessionStorage.setItem("rammy:open_voice", "1"); } catch (_) { /* private mode */ }
    go("chat");
  };

  const markDone = async (r) => {
    setCommitments(prev => prev.filter(x => x.id !== r.id));
    try {
      await api.patch(`/reminders/${r.id}/toggle`);
      toast.success("Done.");
    } catch (_) {
      toast.error("Couldn't mark that done — try again.");
    }
  };
  const snooze = async (r) => {
    setCommitments(prev => prev.filter(x => x.id !== r.id));
    try {
      await api.patch(`/reminders/${r.id}/snooze?hours=3`);
      toast.success("Snoozed for three hours.");
    } catch (_) {
      toast.error("Couldn't snooze that — try again.");
    }
  };

  return (
    <div className="relative flex flex-col min-h-full" data-testid="screen-home">
      <div className="flex-1 px-5 md:px-8 pt-8 pb-6 max-w-3xl mx-auto w-full space-y-6">
        {/* Time-aware greeting. Evening reads as reflection, morning as intention. */}
        <div data-testid="home-greeting">
          <h1
            className="font-display"
            style={{
              fontSize: "var(--rm-text-2xl)",
              lineHeight: 1.16,
              letterSpacing: "var(--rm-tracking-display)",
              fontWeight: 600,
            }}
          >
            {greet}, {firstName}
          </h1>
          <p
            className="mt-1"
            style={{ fontSize: "0.9375rem", color: "var(--rm-text-muted)" }}
            data-testid="home-greeting-sub"
          >
            {thingCount > 0
              ? `${thingCount} thing${thingCount === 1 ? "" : "s"} from today`
              : "Nothing pressing. Tell me something worth remembering."}
          </p>
        </div>

        {/* Promises made to other people — the follow-through layer. */}
        <PromisesPanel promises={promises} onChange={loadPromises}/>

        {/* Promises Rammy spotted in the user's mail, waiting to be confirmed. */}
        <PromiseSuggestions
          suggestions={promiseSuggestions}
          onResolved={() => { loadPromises(); loadPromiseSuggestions(); }}
        />

        {/* Commitments — warm paper, a checkbox, never a separate to-do app. */}
        {commitments.length > 0 && (
          <div className="space-y-3" data-testid="home-commitments">
            {commitments.map(r => (
              <CommitmentCard
                key={r.id}
                reminder={r}
                asOf={dueSummary?.as_of}
                onDone={() => markDone(r)}
                onSnooze={() => snooze(r)}
              />
            ))}
          </div>
        )}

        {/* Emotional callback cards — compassion beats logistics. */}
        {pendingCheckins.length > 0 && pendingCheckins.map(seed => (
          <CheckinCard
            key={seed.id}
            seed={seed}
            onOpenChat={async () => {
              try {
                const r = await api.post(`/checkins/${seed.id}/follow_up`);
                setPendingCheckins(prev => prev.filter(s => s.id !== seed.id));
                const opener = r.data?.opener || "Just checking in — how are you?";
                go("chat");
                try { sessionStorage.setItem("rammy:pending_opener", opener); } catch (_) { /* private-mode */ }
              } catch (e) {
                toast.error("Couldn't open — try again in a sec");
              }
            }}
            onDismiss={async () => {
              try {
                await api.post(`/checkins/${seed.id}/dismiss`);
                setPendingCheckins(prev => prev.filter(s => s.id !== seed.id));
              } catch (_) { /* silent */ }
            }}
          />
        ))}

        {dueSummary && dueSummary.due_next_24h > 0 && commitments.length === 0 && (
          <RemindersDueChip summary={dueSummary} onOpen={() => go("reminders")}/>
        )}

        {/* Continue where you left off */}
        {resume && (
          <button
            onClick={() => resumeConversation ? resumeConversation(resume.id) : go("chat")}
            className="w-full text-left rounded-2xl bg-[var(--rm-surface)] border border-[var(--rm-border)] p-5 hover:border-[var(--rm-brand)] transition-colors group"
            data-testid="home-resume-card"
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.06em] text-[var(--rm-text-faint)]">
                <L.MessageCircleReply size={13}/> Pick it back up
              </div>
              <div className="text-[11px] font-mono text-[var(--rm-text-faint)]" data-testid="home-resume-time">
                {formatRelative(resume.last_at || resume.updated_at)}
              </div>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              {resume.folder && (
                <span
                  className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[var(--rm-bg-subtle)] px-2 py-0.5 text-[11px] text-[var(--rm-text-muted)] max-w-[180px]"
                  data-testid="home-resume-folder"
                >
                  <span>{resume.folder.emoji || "📁"}</span>
                  <span className="truncate">{resume.folder.name}</span>
                </span>
              )}
              <div className="text-[17px] font-medium truncate" data-testid="home-resume-title">
                {resume.title}
              </div>
            </div>
            {resume.last_snippet && (
              <div className="text-sm text-[var(--rm-text-muted)] mt-1.5 line-clamp-2 leading-relaxed" data-testid="home-resume-snippet">
                <span className="mr-1">{resume.last_role === "user" ? "You:" : "Rammy:"}</span>
                {resume.last_snippet}
              </div>
            )}
          </button>
        )}

        {/* On this day — a memory Rammy is giving back, so: warm paper. */}
        {onThisDay?.has && (
          <div className="rm-paper p-5 flex items-center gap-5" data-testid="home-on-this-day">
            {onThisDay.memory.has_image && onThisDay.memory.image_thumb ? (
              <img
                src={`data:${onThisDay.memory.image_mime || "image/jpeg"};base64,${onThisDay.memory.image_thumb}`}
                alt=""
                className="w-20 h-20 rounded-xl object-cover border border-[var(--rm-border-warm)] shrink-0"
                data-testid="home-on-this-day-thumb"
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-[var(--rm-brand-soft)] flex items-center justify-center shrink-0">
                <L.Sparkles size={26} className="text-[var(--rm-brand)]"/>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-mono uppercase tracking-[0.06em] text-[var(--rm-text-faint)]">{onThisDay.label}</div>
              <div className="mt-1 text-[17px] font-medium">{onThisDay.memory.title}</div>
              {onThisDay.memory.detail && (
                <div className="rm-voice mt-1 line-clamp-2">{onThisDay.memory.detail}</div>
              )}
              <button
                onClick={() => go("chat")}
                className="mt-3 text-sm text-[var(--rm-brand)] hover:underline"
                data-testid="home-on-this-day-open"
              >
                Tell Rammy about it
              </button>
            </div>
          </div>
        )}

        {/* Today calendar + Rammy's notes */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rm-border)] p-5" data-testid="home-today">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.06em] text-[var(--rm-text-faint)]">
                <L.CalendarDays size={13}/> Today
              </div>
              <button onClick={()=>go("calendar")} className="text-[11px] text-[var(--rm-brand)] hover:underline" data-testid="home-today-manage">
                {today.connected ? "Manage" : "Connect"}
              </button>
            </div>
            {!today.connected && (
              <div className="mt-3 text-sm text-[var(--rm-text-muted)] leading-relaxed">
                Connect a calendar and Rammy will narrate your day.
              </div>
            )}
            {today.connected && today.events.length === 0 && (
              <div className="mt-3 text-sm text-[var(--rm-text-muted)]">Your day is quiet — enjoy the space.</div>
            )}
            {today.connected && today.events.length > 0 && (
              <div className="mt-3 space-y-2">
                {today.events.slice(0, 5).map((e, i) => (
                  <div key={i} className="flex items-baseline gap-3 text-sm" data-testid={`home-event-${i}`}>
                    <span className="w-16 shrink-0 text-[11px] font-mono text-[var(--rm-text-muted)]">
                      {e.all_day ? "all day" : new Date(e.start).toLocaleTimeString([], {hour:"numeric", minute:"2-digit"}).toLowerCase()}
                    </span>
                    <span className="flex-1 truncate">{e.title}</span>
                  </div>
                ))}
                {today.events.length > 5 && <div className="text-xs text-[var(--rm-text-faint)]">+{today.events.length - 5} more</div>}
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rm-border)] p-5" data-testid="home-notes">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.06em] text-[var(--rm-text-faint)]">
                <L.Sparkles size={13}/> Rammy&rsquo;s notes
              </div>
              {notes.items.length > 0 && (
                <button onClick={()=>go("inbox")} className="text-[11px] text-[var(--rm-brand)] hover:underline" data-testid="home-notes-open">
                  Read all
                </button>
              )}
            </div>
            {notes.loading && <div className="mt-3 text-sm text-[var(--rm-text-faint)]">Loading…</div>}
            {!notes.loading && notes.items.length === 0 && (
              <div className="mt-3 text-sm text-[var(--rm-text-muted)] leading-relaxed">
                Turn on the morning briefing in <button onClick={()=>go("rituals")} className="underline underline-offset-2 text-[var(--rm-brand)]" data-testid="home-notes-setup">Rituals</button> and Rammy will drop a warm note here each day.
              </div>
            )}
            {!notes.loading && notes.items.length > 0 && (
              <div className="mt-3 space-y-3">
                {notes.items.map(n => (
                  <button key={n.id} onClick={()=>go("inbox")} data-testid={`home-note-${n.id}`} className="w-full text-left">
                    <div className="flex items-center gap-2">
                      {n.unread && <span className="w-1.5 h-1.5 rounded-full bg-[var(--rm-brand)] shrink-0"/>}
                      <span className="text-sm font-medium truncate">{n.title}</span>
                    </div>
                    <div className="text-xs text-[var(--rm-text-muted)] mt-0.5 line-clamp-2 leading-relaxed">{n.body}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Focus inbox */}
        <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rm-border)] p-5" data-testid="home-emails">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.06em] text-[var(--rm-text-faint)]">
                <L.Mail size={13}/> Focus inbox
              </div>
              <div className="text-sm text-[var(--rm-text-muted)] mt-1 leading-relaxed">Top three unread — Rammy can draft a reply you approve.</div>
            </div>
            {gmail.items.length > 0 && (
              <button onClick={()=>go("emails")} className="text-[11px] text-[var(--rm-brand)] hover:underline" data-testid="home-emails-open">
                Open
              </button>
            )}
          </div>

          {gmail.loading && <div className="mt-4 text-sm text-[var(--rm-text-faint)]">Reading your inbox…</div>}

          {!gmail.loading && gmailNotConnected && (
            <div className="mt-4 rounded-xl border border-[var(--rm-border)] bg-[var(--rm-bg-subtle)] p-4">
              <div className="text-sm font-medium">Connect Google to see focus inbox</div>
              <p className="text-xs text-[var(--rm-text-muted)] mt-1 leading-relaxed">
                Gmail rides on the same Google connection Rammy uses for Calendar.
              </p>
              <Button onClick={()=>go("calendar")} className="btn-pill btn-secondary h-9 px-4 mt-3 text-xs" data-testid="home-emails-connect">
                Connect Google
              </Button>
            </div>
          )}

          {!gmail.loading && !gmailNotConnected && gmail.items.length === 0 && (
            <div className="mt-4 text-sm text-[var(--rm-text-muted)] leading-relaxed">
              Inbox zero — nothing unread that Rammy needs to worry you about.
            </div>
          )}

          {!gmail.loading && gmail.items.length > 0 && (
            <div className="mt-4 space-y-3">
              {gmail.items.map(e => (
                <button
                  key={e.id}
                  onClick={()=>go("emails")}
                  data-testid={`home-email-${e.id}`}
                  className="w-full text-left rounded-xl border border-[var(--rm-border)] p-3 hover:border-[var(--rm-brand)] transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium truncate">{e.from?.name || e.from?.email || "(unknown)"}</span>
                    <span className="text-[11px] font-mono text-[var(--rm-text-faint)] shrink-0">
                      {e.date ? new Date(e.date).toLocaleDateString([], {day:"numeric", month:"short"}) : ""}
                    </span>
                  </div>
                  <div className="text-sm truncate">{e.subject}</div>
                  <div className="text-xs text-[var(--rm-text-muted)] line-clamp-1 mt-0.5">{e.snippet}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <HomeCard onClick={()=>go("focus")} icon={<L.Sparkles size={18}/>} label="Focus" hint="45-min quiet block"/>
          <HomeCard onClick={()=>go("reminders")} icon={<L.Bell size={18}/>} label="Reminders" hint="Nudge you gently"/>
          <HomeCard onClick={()=>go("memories")} icon={<L.Brain size={18}/>} label="Rings" hint="What Rammy remembers"/>
          <HomeCard onClick={()=>go("integrations")} icon={<L.PlugZap size={18}/>} label="Connections" hint="Calendar · Gmail · Weather"/>
        </div>

        {device && (
          <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rm-border)] p-4">
            <div className="text-[11px] font-mono uppercase tracking-[0.06em] text-[var(--rm-text-faint)] mb-3">Device</div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <StatChip icon={<L.Battery size={16}/>} label="Battery" value={`${device.battery}%`}/>
              <StatChip icon={<L.Wifi size={16}/>} label="WiFi" value={device.wifi}/>
              <StatChip icon={<L.Cpu size={16}/>} label="Firmware" value={device.firmware}/>
            </div>
          </div>
        )}
      </div>

      {/* THE THUMB ZONE — the Ring lives here, never at the top. "Type
          instead" carries equal visual weight: voice/text parity is true in
          the backend and the UI has to say so. */}
      <div
        className="sticky bottom-0 z-10 relative flex flex-col items-center justify-end pb-6"
        style={{ minHeight: "var(--rm-thumb-zone)", background: "var(--rm-bg)" }}
        data-testid="home-thumb-zone"
      >
        {/* Fade so scrolled content dissolves into the thumb zone. */}
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 -top-10 h-10 pointer-events-none"
          style={{ background: "linear-gradient(to top, var(--rm-bg), transparent)" }}
        />
        <Ring
          state="idle"
          size={96}
          showLabel={false}
          onClick={openVoice}
          ariaLabel="Hold to talk to Rammy"
          testId="home-ring"
        />
        <div className="mt-3 text-sm text-[var(--rm-text-muted)]">Hold to talk</div>
        <div className="mt-4 flex items-center gap-6">
          <button
            type="button"
            onClick={() => go("chat")}
            className="inline-flex items-center gap-2 text-sm text-[var(--rm-text-muted)] hover:text-[var(--rm-text)] transition-colors"
            data-testid="home-type-instead"
          >
            <L.Keyboard size={15}/> Type instead
          </button>
          <button
            type="button"
            onClick={() => {
              try { sessionStorage.setItem("rammy:pending_opener", "Recap my day."); } catch (_) { /* private mode */ }
              go("chat");
            }}
            className="inline-flex items-center gap-2 text-sm text-[var(--rm-text-muted)] hover:text-[var(--rm-text)] transition-colors"
            data-testid="home-recap"
          >
            <L.RotateCcw size={15}/> Recap my day
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * CommitmentCard — an extracted commitment on warm paper with a checkbox.
 * Never a separate to-do app: the moment it looks like Todoist we're
 * competing with Todoist. Completion fills marigold, no confetti.
 */
function CommitmentCard({ reminder, asOf, onDone, onSnooze }) {
  const [done, setDone] = useState(false);
  const eta = _reminderETA(reminder.when, asOf);
  return (
    <div className="rm-paper p-5 fade-up" data-testid={`home-commitment-${reminder.id}`}>
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => { setDone(true); setTimeout(onDone, 380); }}
          aria-label="Mark done"
          data-testid={`home-commitment-done-${reminder.id}`}
          className="mt-0.5 w-5 h-5 rounded-full border-2 shrink-0 grid place-items-center transition-colors"
          style={{
            borderColor: done ? "var(--rm-done)" : "var(--rm-border-strong)",
            background: done ? "var(--rm-done)" : "transparent",
          }}
        >
          {done && <L.Check size={12} color="#fff" strokeWidth={3}/>}
        </button>
        <div className="flex-1 min-w-0">
          <div className="rm-voice">{reminder.title}</div>
          {eta && (
            <div className="mt-1.5 text-[11px] font-mono text-[var(--rm-text-faint)]" data-testid={`home-commitment-eta-${reminder.id}`}>
              {eta}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 pl-8">
        <button
          type="button"
          onClick={() => { setDone(true); setTimeout(onDone, 380); }}
          className="h-9 px-4 rounded-full text-sm font-medium bg-[var(--rm-brand)] text-[var(--rm-on-brand)] hover:bg-[var(--rm-brand-hover)] active:scale-[0.98] transition-[background-color,transform]"
          data-testid={`home-commitment-done-btn-${reminder.id}`}
        >
          Done
        </button>
        <button
          type="button"
          onClick={onSnooze}
          className="h-9 px-4 rounded-full text-sm text-[var(--rm-text-muted)] hover:text-[var(--rm-text)] hover:bg-[var(--rm-bg-subtle)] transition-colors"
          data-testid={`home-commitment-snooze-${reminder.id}`}
        >
          Snooze
        </button>
      </div>
    </div>
  );
}

export function HomeCard({ icon, label, hint, onClick }) {
  return (
    <button onClick={onClick} data-testid={`home-card-${label.toLowerCase()}`} className="text-left rounded-2xl bg-[var(--rm-surface)] border border-[var(--rm-border)] p-4 hover:border-[var(--rm-brand)] transition-colors">
      <div className="w-9 h-9 rounded-xl bg-[var(--rm-bg-subtle)] flex items-center justify-center text-[var(--rm-brand)]">{icon}</div>
      <div className="mt-3 font-medium">{label}</div>
      <div className="text-xs text-[var(--rm-text-muted)]">{hint}</div>
    </button>
  );
}

export function StatChip({ icon, label, value }) {
  return (
    <div className="rounded-2xl bg-[var(--rammy-surface-alt)] p-3">
      <div className="flex items-center justify-center text-[var(--rammy-text-muted)]">{icon}</div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] mt-1">{label}</div>
      <div className="text-sm font-medium truncate">{value}</div>
    </div>
  );
}

export function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

// Compact "just now / 12m / 3h / 2d ago" formatter for the resume card.
function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  const m = Math.round(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}


/** Format an ISO timestamp into a warm relative string aimed at the
 *  reminders-due preview ("2h overdue", "in 40m", "now"). */
function _reminderETA(iso, asOf) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = asOf ? new Date(asOf).getTime() : Date.now();
  if (Number.isNaN(then)) return "";
  const diffMin = Math.round((then - now) / 60000);
  if (diffMin >= -1 && diffMin <= 1) return "now";
  if (diffMin < 0) {
    const past = -diffMin;
    if (past < 60) return `${past}m overdue`;
    const h = Math.round(past / 60);
    if (h < 24) return `${h}h overdue`;
    const d = Math.round(h / 24);
    return `${d}d overdue`;
  }
  if (diffMin < 60) return `in ${diffMin}m`;
  const h = Math.round(diffMin / 60);
  return `in ${h}h`;
}

/** RemindersDueChip — ambient "you have reminders due" surface for Home.
 *
 * Two-state UI:
 *  1. If `due_now > 0`: red-hot amber card highlighting the overdue count.
 *  2. Else `due_next_24h > 0`: calm mint card with the "coming up" count.
 *
 * Clicking either navigates to the Reminders screen.
 */
function RemindersDueChip({ summary, onOpen }) {
  const overdue = summary.due_now || 0;
  const soon = summary.due_next_24h || 0;
  const preview = summary.preview || [];
  const hot = overdue > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="home-reminders-due-chip"
      className={`w-full text-left rounded-3xl border p-4 md:p-5 transition group rammy-shadow flex items-start gap-3 ${
        hot
          ? "bg-amber-50 border-amber-300 hover:border-amber-400"
          : "bg-[var(--rammy-cyan)]/8 border-[var(--rammy-cyan)]/40 hover:border-[var(--rammy-cyan)]"
      }`}
    >
      <div
        className={`shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center ${
          hot ? "bg-amber-500/20 text-amber-700" : "bg-[var(--rammy-cyan)]/25 text-[var(--rammy-cyan-dark)]"
        }`}
      >
        <L.BellRing size={20}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold ${
          hot ? "text-amber-700" : "text-[var(--rammy-cyan-dark)]"
        }`}>
          {hot ? "Reminders due" : "Coming up today"}
          <span className="text-[var(--rammy-text-muted)] normal-case tracking-normal text-[10px] font-normal">
            {hot && soon > 0 ? `· ${soon} more in the next 24h` : null}
          </span>
        </div>
        <div className="mt-0.5 text-[15px] md:text-base text-[var(--rammy-text)] font-medium truncate" data-testid="home-reminders-due-headline">
          {hot
            ? `${overdue} reminder${overdue === 1 ? "" : "s"} waiting for you`
            : `${soon} reminder${soon === 1 ? "" : "s"} in the next 24 hours`}
        </div>

        {/* Titles of up to 3 due-right-now items */}
        {preview.length > 0 && (
          <ul className="mt-1 space-y-0.5" data-testid="home-reminders-due-preview">
            {preview.slice(0, 3).map((r) => (
              <li key={r.id} className="text-[12px] text-[var(--rammy-text-sec)] flex items-center gap-2">
                <span className={`w-1 h-1 rounded-full ${hot ? "bg-amber-500" : "bg-[var(--rammy-cyan-dark)]"}`}/>
                <span className="truncate flex-1">{r.title}</span>
                <span className={`text-[10px] font-mono shrink-0 ${
                  hot ? "text-amber-700" : "text-[var(--rammy-cyan-dark)]"
                }`}>{_reminderETA(r.when, summary.as_of)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <L.ChevronRight size={16} className="text-[var(--rammy-text-muted)] shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform"/>
    </button>
  );
}


/**
 * CheckinCard — the softest ambient callback on Home when the user shared
 * something hard a few days back. Warm palette, no urgency, two clearly
 * differentiated actions: "How I'm doing" opens chat with a composed
 * opener; "Not now" dismisses the seed for good.
 *
 * The visual language is intentionally CALMER than the reminder chip above
 * — muted cyan-lavender gradient, slower fade-in — because a compassionate
 * re-check has to feel like it was authored, not automated.
 */
function CheckinCard({ seed, onOpenChat, onDismiss }) {
  const daysAgo = Math.max(1, Math.floor((Date.now() - new Date(seed.created_at).getTime()) / (24 * 3600 * 1000)));
  const label =
    seed.kind === "sad"     ? "Something felt heavy"
    : seed.kind === "anxious" ? "You were feeling anxious"
    : "You shared something hard";
  const entityBit = seed.entities && seed.entities.length ? ` about ${seed.entities[0]}` : "";
  return (
    <div
      className="rounded-3xl border border-[var(--rammy-cyan)]/25 bg-gradient-to-br from-[var(--rammy-cyan)]/10 via-white to-[var(--rammy-cyan)]/5 p-5 flex items-start gap-4 rammy-shadow"
      data-testid={`home-checkin-${seed.id}`}
    >
      <div className="w-10 h-10 rounded-2xl bg-[var(--rm-surface)]/70 border border-[var(--rammy-cyan)]/25 flex items-center justify-center text-[var(--rammy-cyan-dark)] shrink-0">
        <L.HeartHandshake size={18}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">
          {label}{entityBit} · {daysAgo} day{daysAgo === 1 ? "" : "s"} ago
        </div>
        <div className="mt-1 text-[15px] leading-snug text-[var(--rammy-text)]">
          Just checking in — how has that been sitting with you?
        </div>
        {seed.snippet && (
          <div className="mt-1 text-[11.5px] text-[var(--rammy-text-muted)] italic truncate">
            you said: “{seed.snippet}”
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onOpenChat}
            data-testid={`home-checkin-open-${seed.id}`}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[var(--rammy-cyan-dark)] text-white text-[12.5px] font-semibold hover:opacity-90 transition"
          >
            <L.MessageCircle size={13}/> How I&apos;m doing
          </button>
          <button
            type="button"
            onClick={onDismiss}
            data-testid={`home-checkin-dismiss-${seed.id}`}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[var(--rammy-border)] bg-[var(--rm-surface)] text-[12.5px] text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] transition"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
